import { Inject, Injectable } from '@angular/core';
import type { Chart, HTMLDOMElement } from 'highcharts';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { HighchartsLazy, HIGHCHARTS_LAZY } from './highcharts-lazy.token';
import { HighchartsOptions } from '@app/charts/components/charts.model';

const CHART_COLORS = {
  lightCoral: '#FDDCD6',
  coral: '#F54E31',
  darkCoral: '#C2341B',
  // orange
  lightOrange: '#FAE3CC',
  orange: '#E87400',
  darkOrange: '#B54E00',
  // forest green
  lightForestGreen: '#D8EECD',
  forestGreen: '#3DA807',
  darkForestGreen: '#297504',
  // ocean blue
  lightOceanBlue: '#CCEDED',
  oceanBlue: '#00A6A6',
  darkOceanBlue: '#007373',
  // sky blue
  lightSkyBlue: '#CCECF7',
  skyBlue: '#009FD9',
  darkSkyBlue: '#007099',
  // purple
  lightPurple: '#E5D9F9',
  purple: '#7F43E0',
  darkPurple: '#562AAD',
  // fuschia
  lightFuschia: '#F1D7F2',
  fuschia: '#B844BD',
  darkFuschia: '#85128A',
  // pink
  lightPink: '#FBD8E8',
  pink: '#EB3D7D',
  darkPink: '#B81C62',
};

@Injectable({ providedIn: 'root' })
export class HighchartsService {
  public colors = CHART_COLORS;

  private highcharts = from(this.highchartsLazy);

  constructor(
    @Inject(HIGHCHARTS_LAZY) private highchartsLazy: HighchartsLazy,
    private translate: TranslateService
  ) {}

  /**
   * Ensure the lazy-loaded highcharts service is ready, then create the chart
   * @param element - the element to render the chart in
   * @param options - the options for the chart
   * TODO: use typing HighchartsOptions.
   *       unfortunately it causes a lot of issues right now
   *       with skill-sections-chart.service (and maybe others)
   *       this is due to at least one of the config options being invalid
   *
   * @param chartTitle - the title of the chart
   */
  public createChart(
    element: string | HTMLDOMElement,
    options: any,
    chartTitle: string,
    a11yChartDescription: string
  ): Observable<Chart> {
    return this.highcharts.pipe(
      switchMap(
        (highcharts) =>
          new Observable<Chart>((subscriber) => {
            const callback = (chart: Chart) => {
              subscriber.next(chart);
              subscriber.complete();
            };

            const containerLabel = this.translate.instant(
              'A11y_InteractiveChart',
              {
                chartTitle,
              }
            );

            // allow for other properties to be passed down
            // set the chart and svg container labels to the chart title
            const highchartsOptions: HighchartsOptions = {
              ...options,
              lang: {
                ...options?.lang,
                accessibility: {
                  ...options?.lang?.accessibility,
                  chartContainerLabel: `${containerLabel}`,
                  svgContainerLabel: `${containerLabel}`,
                  defaultChartTitle: chartTitle,
                  //remove default chart descriptors
                  axis: null,
                  chartTypes: null,
                },
              },
              accessibility: {
                ...options?.accessibility,
                description: a11yChartDescription,
              },
            };

            if (element) {
              highcharts.chart(element, highchartsOptions, callback);
            } else {
              highcharts.chart(highchartsOptions, callback);
            }
          })
      )
    );
  }

  public getChartColors() {
    return this.colors;
  }
}
